/**
 * 설정 > 메뉴 관리.
 * Figma「18 메뉴 설정 관리」(13079:3589) — 트리 DnD(@dnd-kit) + 메뉴 상세 폼.
 * GET/POST/PATCH/DELETE /admin/menus · PUT /admin/menus/tree (ROLE_MASTER).
 * 트리 순서·계층·상세 속성 변경은 우측 「저장」에서 필요한 API만 호출한다.
 */
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useCreateMenu,
  useDeleteMenu,
  useMenuDetail,
  useMenuTree,
  useReorderMenuTree,
  useUpdateMenu,
} from '@/entities/menu/hooks/useMenus';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/shared/components/ui/alert';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import {
  CornerDownRight,
  GripVertical,
  IndentDecrease,
  IndentIncrease,
  Info,
  Menu,
  Plus,
  TriangleAlert,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CreateMenuModal, type CreateMenuFormInput } from './CreateMenuModal';
import {
  flattenAdminMenuTree,
  menuTreeStructureSignature,
  mergeTreeResponseWithLocal,
  toMenuTreeReorderRequest,
  toMenuUpsertRequest,
} from './menuTreeApi';
import {
  applyMenuTreeDrag,
  collectUsedMenuUrls,
  DEFAULT_MENU_ICON_KEY,
  flattenMenuItems,
  fromMenuUrlSelectValue,
  getMenuProjection,
  hasChildMenus,
  INDENT_WIDTH,
  indentMenuItem,
  isUnmappedMenuUrl,
  listAvailableMenuUrlOptions,
  listDirectChildMenus,
  listDragVisibleItems,
  listParentOptions,
  listUnmappedMenuItems,
  outdentMenuItem,
  reparentMenuItem,
  resolveGroupedRootOverId,
  resolveMenuDropIndicator,
  resolveMenuIcon,
  resolveNewMenuParentId,
  ROOT_PARENT_VALUE,
  toMenuUrlSelectValue,
  type MenuAudience,
  type MenuDragIntent,
  type MenuDropEdge,
  type MenuIconKey,
  type MenuProjection,
  type MenuTreeItem,
} from './settingsMenuData';
type MenuDraft = {
  id: string;
  name: string;
  menuKey: string;
  url: string;
  parentId: string | null;
  visible: boolean;
  audience: MenuAudience;
  iconKey: MenuIconKey;
  sectionLabel?: string;
  systemDefined: boolean;
  hasChildren: boolean;
  parentMenuName: string | null;
  isNew: boolean;
};

const toDraft = (
  item: MenuTreeItem,
  extras?: Partial<
    Pick<
      MenuDraft,
      'systemDefined' | 'hasChildren' | 'parentMenuName' | 'isNew'
    >
  >,
): MenuDraft => ({
  id: item.id,
  name: item.name,
  menuKey: item.menuKey,
  url: item.url,
  parentId: item.parentId,
  visible: item.visible,
  audience: item.audience,
  iconKey: item.iconKey || DEFAULT_MENU_ICON_KEY,
  sectionLabel: item.sectionLabel,
  systemDefined: extras?.systemDefined ?? item.systemDefined ?? false,
  hasChildren:
    extras?.hasChildren ??
    item.hasChildrenFlag ??
    false,
  parentMenuName: extras?.parentMenuName ?? item.parentMenuName ?? null,
  isNew: extras?.isNew ?? false,
});

const getApiErrorStatus = (error: unknown): number | null => {
  if (!isAxiosError(error)) return null;
  return error.response?.status ?? null;
};
const intentBadgeClass = (intent: MenuDragIntent): string => {
  switch (intent) {
    case 'nest':
      return 'bg-ruda-primary-50 text-primary';
    case 'promote':
      return 'bg-ruda-success-50 text-ruda-success';
    case 'blocked-nest':
      return 'bg-ruda-error-50 text-ruda-error';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const SegmentedToggle = <T extends string>({
  value,
  options,
  onChange,
  activeClassName,
  disabled = false,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  activeClassName?: string;
  disabled?: boolean;
}) => (
  <div className="inline-flex rounded-lg bg-muted p-[3px]">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={cn(
            'rounded-md px-[18px] py-[7px] text-xs transition-colors',
            active
              ? cn(
                  'border border-border bg-background font-medium shadow-sm',
                  activeClassName,
                )
              : 'font-normal text-muted-foreground',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

type MenuDropMode =
  | 'none'
  /** 하위가 딸린 대분류 블록 순서 변경 */
  | 'root-block'
  /** 최상위(대분류)로 승격 — 좌측 풀폭·성공색 */
  | 'promote'
  /** 다른 대분류의 하위로 편입 — 들여쓰기 라인 */
  | 'nest'
  /** 같은 계층에서 순서만 변경 */
  | 'reorder';

type SortableMenuRowItemProps = {
  item: MenuTreeItem;
  depth: number;
  selected: boolean;
  childCount: number;
  canIndent: boolean;
  canOutdent: boolean;
  isNestTarget: boolean;
  dropMode?: MenuDropMode;
  dropLineDepth: number;
  /** 삽입 갭이 행의 위인지 아래인지 (순서 변경·블록 이동) */
  dropEdge?: MenuDropEdge;
  /** 대분류 드래그 중 — 하위가 접혀 그룹으로 이동 중임을 표시 */
  groupedDragging?: boolean;
  /** 메뉴 WRITE 없을 때 DnD·들여쓰기 비활성 */
  dragDisabled?: boolean;
  onSelect: (id: string) => void;
  onIndent: (id: string) => void;
  onOutdent: (id: string) => void;
};

const DropIndicator: React.FC<{
  mode: MenuDropMode;
  dropLineDepth: number;
  edge: MenuDropEdge;
}> = ({ mode, dropLineDepth, edge }) => {
  if (mode === 'none') return null;

  const edgeClass =
    edge === 'after' ? 'absolute inset-x-0 bottom-0 z-20' : 'absolute inset-x-0 top-0 z-20';
  // after일 때 칩은 라인 위쪽(행 안)에 두어 다음 행과 겹치지 않게 한다
  const chipY = edge === 'after' ? 'bottom-2' : 'top-2';

  if (mode === 'root-block') {
    return (
      <div aria-hidden className={cn('pointer-events-none', edgeClass)}>
        <div className="h-1.5 w-full bg-primary" />
        <span
          className={cn(
            'absolute left-2 rounded-md bg-primary px-2 py-0.5 text-[10px] font-semibold leading-none text-white shadow-sm',
            chipY,
          )}
        >
          여기에 대분류 블록 삽입
        </span>
      </div>
    );
  }

  if (mode === 'promote') {
    // 최상위 자리: 왼쪽부터 풀폭, 성공색으로 승격임을 표시
    return (
      <div aria-hidden className={cn('pointer-events-none', edgeClass)}>
        <div className="h-1.5 w-full bg-ruda-success" />
        <span
          className={cn(
            'absolute left-2 rounded-md bg-ruda-success px-2 py-0.5 text-[10px] font-semibold leading-none text-white shadow-sm',
            chipY,
          )}
        >
          최상위(대분류)로 승격
        </span>
      </div>
    );
  }

  if (mode === 'nest') {
    // 하위 편입: grip 옆부터 depth 1만큼 들여쓴 위치 (항상 행 상단)
    const indent = 40 + Math.max(dropLineDepth, 1) * INDENT_WIDTH;
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-20"
        style={{ paddingLeft: indent, paddingRight: 12 }}
      >
        <div className="relative h-1 rounded-full bg-primary">
          <span className="absolute left-0 top-2 whitespace-nowrap rounded-md bg-primary px-2 py-0.5 text-[10px] font-semibold leading-none text-white shadow-sm">
            하위로 편입
          </span>
        </div>
      </div>
    );
  }

  // reorder: 행 좌측 끝부터 풀폭 중립 라인 — 실제 삽입 갭(위/아래)
  return (
    <div aria-hidden className={cn('pointer-events-none', edgeClass)}>
      <div className="h-0.5 w-full bg-slate-400" />
    </div>
  );
};

const SortableMenuRowItem: React.FC<SortableMenuRowItemProps> = ({
  item,
  depth,
  selected,
  childCount,
  canIndent,
  canOutdent,
  isNestTarget,
  dropMode = 'none',
  dropLineDepth,
  dropEdge = 'before',
  groupedDragging = false,
  dragDisabled = false,
  onSelect,
  onIndent,
  onOutdent,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: dragDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const RowIcon = resolveMenuIcon(item.iconKey);
  const urlUnmapped = isUnmappedMenuUrl(item.url);

  return (
    <div className="relative" ref={setNodeRef} style={style}>
      <DropIndicator
        mode={dropMode}
        dropLineDepth={dropLineDepth}
        edge={dropEdge}
      />
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-label={
          urlUnmapped
            ? `${item.name} 선택 · 연결 경로 수정 필요`
            : `${item.name} 선택`
        }
        onClick={() => onSelect(item.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(item.id);
          }
        }}
        className={cn(
          'group flex cursor-pointer items-center gap-1.5 border-t border-border px-3 py-[10px]',
          'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          selected && 'border-l-2 border-l-primary bg-ruda-primary-50 hover:bg-ruda-primary-50',
          urlUnmapped &&
            !selected &&
            'bg-ruda-warning/5 hover:bg-ruda-warning/10',
          isNestTarget &&
            'bg-ruda-primary-50/80 ring-1 ring-inset ring-primary/40',
          dropMode === 'root-block' &&
            'bg-primary/[0.06] ring-1 ring-inset ring-primary/30',
          dropMode === 'promote' &&
            'bg-ruda-success-50/80 ring-1 ring-inset ring-ruda-success/35',
          dropMode === 'nest' &&
            !isNestTarget &&
            'bg-ruda-primary-50/40',
          isDragging && 'z-10 bg-background opacity-30',
          groupedDragging &&
            'border-l-2 border-l-primary bg-primary/[0.04]',
          !item.visible && !selected && 'text-muted-foreground',
        )}
      >
        {!dragDisabled ? (
          <button
            type="button"
            className="touch-none shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={`${item.name} 드래그하여 순서·계층 변경`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : (
          <span className="inline-block w-4 shrink-0" aria-hidden />
        )}

        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 text-left text-[13px]',
            selected && 'font-medium text-foreground',
          )}
          style={{ paddingLeft: depth * INDENT_WIDTH }}
        >
          {depth > 0 ? (
            <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : null}
          <RowIcon
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <span className="truncate">{item.name}</span>
          <Badge
            variant="secondary"
            className={cn(
              'shrink-0 rounded-full px-1.5 py-0 text-[10px] font-medium',
              depth === 0
                ? 'bg-slate-100 text-slate-600'
                : 'bg-ruda-primary-50 text-primary',
            )}
          >
            {depth === 0 ? '대분류' : '하위'}
          </Badge>
          {urlUnmapped ? (
            <Badge
              variant="secondary"
              className="shrink-0 rounded-full border border-ruda-warning/40 bg-ruda-warning/15 px-1.5 py-0 text-[10px] font-semibold text-ruda-warning"
              title={`앱 미등록 경로: ${item.url}`}
            >
              경로 수정
            </Badge>
          ) : null}
          {childCount > 0 ? (
            <Badge
              variant="secondary"
              className="shrink-0 rounded-full px-1.5 py-0 text-[10px] font-medium"
            >
              {groupedDragging
                ? `하위 ${childCount} · 함께 이동`
                : `하위 ${childCount}`}
            </Badge>
          ) : null}
          {selected ? (
            <span className="shrink-0 text-[11px] font-medium text-primary">
              편집 중
            </span>
          ) : null}
          {!item.visible ? (
            <Badge
              variant="secondary"
              className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium"
            >
              숨김
            </Badge>
          ) : null}
        </div>

        {!dragDisabled ? (
          <div
            className="flex shrink-0 items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={!canOutdent}
              title="최상위로 올리기 (←)"
              aria-label={`${item.name} 최상위로 올리기`}
              onClick={() => onOutdent(item.id)}
            >
              <IndentDecrease className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={!canIndent}
              title="하위로 내리기 (→)"
              aria-label={`${item.name} 하위로 내리기`}
              onClick={() => onIndent(item.id)}
            >
              <IndentIncrease className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const MenuRowPreview: React.FC<{
  item: MenuTreeItem;
  depth: number;
  projection: MenuProjection | null;
  childrenItems: MenuTreeItem[];
}> = ({ item, depth, projection, childrenItems }) => (
  <div
    className={cn(
      'w-[min(520px,90vw)] overflow-hidden rounded-lg border bg-background shadow-lg',
      childrenItems.length > 0
        ? 'border-2 border-primary shadow-primary/20'
        : 'border-primary/30',
    )}
  >
    <div className="flex items-center gap-2 px-3 py-2.5">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <div
        className="flex min-w-0 flex-1 items-center gap-2 text-[13px] font-medium"
        style={{ paddingLeft: depth * INDENT_WIDTH }}
      >
        <span className="truncate">{item.name}</span>
        {childrenItems.length > 0 ? (
          <Badge
            variant="secondary"
            className="shrink-0 rounded-full px-1.5 py-0 text-[10px]"
          >
            하위 {childrenItems.length} · 함께 이동
          </Badge>
        ) : null}
      </div>
      {projection ? (
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
            intentBadgeClass(projection.intent),
          )}
        >
          {projection.message}
        </span>
      ) : null}
    </div>
    {childrenItems.length > 0 ? (
      <div className="border-t border-border bg-muted/40 px-3 py-1.5">
        {childrenItems.map((child) => (
          <div
            key={child.id}
            className="flex items-center gap-1.5 py-1 text-[12px] text-muted-foreground"
          >
            <CornerDownRight className="h-3 w-3 shrink-0" />
            <span className="truncate">{child.name}</span>
          </div>
        ))}
      </div>
    ) : null}
  </div>
);

export const SettingsMenusPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { canWrite, readOnly } = useMenuAccess();
  const treeQuery = useMenuTree();
  const createMenu = useCreateMenu();
  const updateMenu = useUpdateMenu();
  const deleteMenu = useDeleteMenu();
  const reorderTree = useReorderMenuTree();

  const [items, setItems] = useState<MenuTreeItem[]>([]);
  /** 마지막 서버 동기화 시점의 트리 — 취소·dirty 비교 SSOT */
  const [baselineItems, setBaselineItems] = useState<MenuTreeItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<MenuDraft | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  /** 저장 중 트리 쿼리 동기화 스킵 (mutation 캐시 갱신으로 로컬 dirty가 지워지지 않게) */
  const savingRef = useRef(false);

  const selectedNumericId =
    selectedId && /^\d+$/.test(selectedId) ? Number(selectedId) : null;
  const detailQuery = useMenuDetail(selectedNumericId);

  const handleMenusForbidden = (error: unknown) => {
    if (getApiErrorStatus(error) !== 403) return false;
    toast.error('메뉴 설정 권한이 없습니다');
    navigate({ to: ROUTE_PATHS.MY_ACCOUNT as never });
    return true;
  };

  // 서버 트리 → 로컬. 저장·생성·삭제 후 쿼리 데이터가 바뀌면 기준선도 갱신한다.
  useEffect(() => {
    if (!treeQuery.data?.menus) return;
    if (savingRef.current) return;
    const flat = flattenAdminMenuTree(treeQuery.data.menus);
    setItems(flat);
    setBaselineItems(flat);
    setHydrated(true);
    setSelectedId((prev) => {
      if (prev && flat.some((m) => m.id === prev)) return prev;
      return flat[0]?.id ?? null;
    });
  }, [treeQuery.data]);

  useEffect(() => {
    if (!treeQuery.isError) return;
    if (handleMenusForbidden(treeQuery.error)) return;
    handleApiError(treeQuery.error, toast);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast/navigate 안정
  }, [treeQuery.isError, treeQuery.error]);

  // 상세 조회로 systemDefined / hasChildren / parentMenuName 반영
  useEffect(() => {
    if (!detailQuery.data || !selectedId) return;
    const d = detailQuery.data;
    if (String(d.id) !== selectedId) return;
    setItems((prev) =>
      prev.map((item) =>
        item.id === selectedId
          ? {
              ...item,
              name: d.menuName,
              menuKey: d.menuKey,
              url: d.urlPath ?? '',
              parentId: d.parentId != null ? String(d.parentId) : null,
              visible: d.visible,
              audience: d.accessScope === 'ADMIN_ONLY' ? 'admin' : 'general',
              systemDefined: d.systemDefined,
              hasChildrenFlag: d.hasChildren,
              parentMenuName: d.parentMenuName,
            }
          : item,
      ),
    );
    setDraft((prev) => {
      if (!prev || prev.id !== selectedId) return prev;
      return {
        ...prev,
        name: d.menuName,
        menuKey: d.menuKey,
        url: d.urlPath ?? '',
        parentId: d.parentId != null ? String(d.parentId) : null,
        visible: d.visible,
        audience: d.accessScope === 'ADMIN_ONLY' ? 'admin' : 'general',
        systemDefined: d.systemDefined,
        hasChildren: d.hasChildren,
        parentMenuName: d.parentMenuName,
        isNew: false,
      };
    });
  }, [detailQuery.data, selectedId]);

  useEffect(() => {
    if (!detailQuery.isError) return;
    const status = getApiErrorStatus(detailQuery.error);
    if (status === 403 && handleMenusForbidden(detailQuery.error)) return;
    if (status === 404) {
      toast.warning('삭제된 메뉴입니다. 트리를 다시 불러옵니다');
      void treeQuery.refetch();
      setDraft(null);
      setSelectedId(null);
      return;
    }
    handleApiError(detailQuery.error, toast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQuery.isError, detailQuery.error]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const flatItems = useMemo(() => flattenMenuItems(items), [items]);
  const visibleItems = useMemo(
    () => listDragVisibleItems(items, activeId),
    [items, activeId],
  );
  const visibleFlatItems = useMemo(
    () => flattenMenuItems(visibleItems),
    [visibleItems],
  );
  const sortableIds = useMemo(
    () => visibleItems.map((m) => m.id),
    [visibleItems],
  );
  const childCountByParent = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      if (!item.parentId) continue;
      map.set(item.parentId, (map.get(item.parentId) ?? 0) + 1);
    }
    return map;
  }, [items]);

  const isTreeDirty =
    hydrated &&
    menuTreeStructureSignature(items) !==
      menuTreeStructureSignature(baselineItems);

  /**
   * 상세 속성 dirty — name/url/visible/audience만.
   * parentId·순서는 트리 구조(signature)로 판별해 PUT /menus/tree 로 보낸다.
   */
  const isAttributeDirty = useMemo(() => {
    if (!draft) return false;
    const current = items.find((m) => m.id === draft.id);
    if (!current) return false;
    return (
      draft.name !== current.name ||
      draft.url !== current.url ||
      draft.visible !== current.visible ||
      draft.audience !== current.audience
    );
  }, [draft, items]);

  const hasPendingChanges = isTreeDirty || isAttributeDirty;
  const isSaving =
    saving || updateMenu.isPending || reorderTree.isPending;

  const activeItem = activeId
    ? (items.find((m) => m.id === activeId) ?? null)
    : null;
  const activeChildren = useMemo(() => {
    if (!activeId) return [];
    return listDirectChildMenus(items, activeId);
  }, [items, activeId]);
  const isGroupedRootDrag = Boolean(
    activeItem && activeItem.parentId === null && activeChildren.length > 0,
  );

  const projection = useMemo(() => {
    if (!activeId || !overId) return null;
    const offset = isGroupedRootDrag ? 0 : offsetLeft;
    const resolvedOver = resolveGroupedRootOverId(items, activeId, overId);
    return getMenuProjection(
      visibleFlatItems,
      activeId,
      resolvedOver,
      offset,
    );
  }, [
    activeId,
    overId,
    offsetLeft,
    visibleFlatItems,
    items,
    isGroupedRootDrag,
  ]);

  const resolvedOverId = useMemo(() => {
    if (!activeId || !overId) return null;
    return resolveGroupedRootOverId(items, activeId, overId);
  }, [activeId, overId, items]);

  const dropIndicator = useMemo(() => {
    const intent = projection?.intent;
    if (
      !activeId ||
      !resolvedOverId ||
      !intent ||
      intent === 'blocked-nest' ||
      intent === 'nest'
    ) {
      return null;
    }
    return resolveMenuDropIndicator(
      items,
      visibleItems,
      activeId,
      resolvedOverId,
    );
  }, [activeId, resolvedOverId, projection?.intent, items, visibleItems]);

  const parentOptions = useMemo(
    () => listParentOptions(items, draft?.id ?? null),
    [items, draft],
  );
  const usedUrls = useMemo(() => collectUsedMenuUrls(items), [items]);
  const unmappedMenus = useMemo(() => listUnmappedMenuItems(items), [items]);
  const urlOptions = useMemo(
    () => listAvailableMenuUrlOptions(usedUrls, draft?.url),
    [usedUrls, draft?.url],
  );
  const draftUrlUnmapped = Boolean(draft && isUnmappedMenuUrl(draft.url));

  useEffect(() => {
    if (!draft) return;
    const current = items.find((m) => m.id === draft.id);
    if (!current) {
      setDraft(null);
      setSelectedId(null);
      return;
    }
    if (draft.parentId !== current.parentId) {
      setDraft((prev) =>
        prev ? { ...prev, parentId: current.parentId } : prev,
      );
    }
  }, [items, draft]);

  // 선택 변경 시 draft 초기화 (상세 API 도착 전 트리 값으로 먼저 표시)
  useEffect(() => {
    if (!selectedId) {
      setDraft(null);
      return;
    }
    const item = items.find((m) => m.id === selectedId);
    if (!item) return;
    setDraft((prev) => {
      if (prev?.id === selectedId) return prev;
      return toDraft(item, {
        hasChildren: hasChildMenus(items, item.id) || item.hasChildrenFlag,
      });
    });
    setNameError(null);
  }, [selectedId, items]);

  const selectItem = (id: string) => {
    setSelectedId(id);
  };

  const handleAddMenu = () => {
    setCreateOpen(true);
  };

  const handleCreateMenu = async (
    input: CreateMenuFormInput,
  ): Promise<string | null> => {
    try {
      const created = await createMenu.mutateAsync(
        toMenuUpsertRequest(input),
      );
      toast.success('메뉴가 추가되었습니다');
      setCreateOpen(false);
      setSelectedId(String(created.id));
      await treeQuery.refetch();
      return null;
    } catch (error) {
      if (handleMenusForbidden(error)) return '권한이 없습니다';
      const status = getApiErrorStatus(error);
      if (status === 400) {
        handleApiError(error, toast);
        return '입력값을 확인해주세요';
      }
      handleApiError(error, toast);
      return '메뉴 추가에 실패했습니다';
    }
  };

  const createDefaultParentId = useMemo(
    () => resolveNewMenuParentId(items, selectedId),
    [items, selectedId],
  );

  const handleCancel = () => {
    setItems(baselineItems);
    if (draft) {
      const base = baselineItems.find((m) => m.id === draft.id);
      if (base) {
        setDraft(
          toDraft(base, {
            systemDefined: draft.systemDefined,
            hasChildren: draft.hasChildren,
            parentMenuName: draft.parentMenuName,
          }),
        );
      } else {
        setDraft(null);
        setSelectedId(baselineItems[0]?.id ?? null);
      }
    }
    setNameError(null);
  };

  const handleSave = async () => {
    const attributeDirty = isAttributeDirty;
    const treeDirty = isTreeDirty;

    if (!attributeDirty && !treeDirty) return;

    if (attributeDirty) {
      if (!draft) return;
      const name = draft.name.trim();
      if (!name) {
        setNameError('메뉴명을 입력해주세요');
        return;
      }
      if (name.length > 100) {
        setNameError('메뉴명은 최대 100자입니다');
        return;
      }
      if (isUnmappedMenuUrl(draft.url)) {
        toast.warning(
          '연결 경로가 앱에 없습니다. 프론트 정의 경로로 바꾼 뒤 저장하세요.',
        );
        return;
      }
    }
    setNameError(null);
    savingRef.current = true;
    setSaving(true);

    try {
      // 속성 변경분을 items에 먼저 반영한 뒤 트리 PUT에 사용
      let workingItems = items;
      if (attributeDirty && draft) {
        workingItems = items.map((item) =>
          item.id === draft.id
            ? {
                ...item,
                name: draft.name.trim(),
                url: draft.url.trim(),
                visible: draft.visible,
                audience: draft.audience,
                parentId: draft.parentId,
              }
            : item,
        );
      }

      // 계층·순서는 PUT, 메타는 PATCH — 트리 먼저 반영해 parentId 정합을 맞춤
      if (treeDirty) {
        const response = await reorderTree.mutateAsync(
          toMenuTreeReorderRequest(workingItems),
        );
        workingItems = mergeTreeResponseWithLocal(
          response.menus,
          workingItems,
        );
      }

      if (attributeDirty && draft) {
        await updateMenu.mutateAsync({
          menuId: Number(draft.id),
          body: toMenuUpsertRequest({
            name: draft.name,
            parentId: draft.parentId,
            url: draft.url,
            visible: draft.visible,
            audience: draft.audience,
          }),
        });
      }

      setItems(workingItems);
      setBaselineItems(workingItems);
      if (draft) {
        const nextDraftItem = workingItems.find((m) => m.id === draft.id);
        if (nextDraftItem) {
          setDraft(
            toDraft(nextDraftItem, {
              systemDefined: draft.systemDefined,
              hasChildren: draft.hasChildren,
              parentMenuName: draft.parentMenuName,
            }),
          );
        }
      }
      toast.success('메뉴가 저장되었습니다');
    } catch (error) {
      if (handleMenusForbidden(error)) return;
      const status = getApiErrorStatus(error);
      if (status === 404) {
        toast.warning('삭제된 메뉴입니다. 트리를 다시 불러옵니다');
        await treeQuery.refetch();
        setDraft(null);
        setSelectedId(null);
        return;
      }
      handleApiError(error, toast);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft) return;
    if (draft.systemDefined || draft.hasChildren) return;
    try {
      await deleteMenu.mutateAsync(Number(draft.id));
      toast.success('메뉴가 삭제되었습니다');
      setDraft(null);
      setSelectedId(null);
      await treeQuery.refetch();
    } catch (error) {
      if (handleMenusForbidden(error)) return;
      const status = getApiErrorStatus(error);
      if (status === 404) {
        toast.warning('이미 삭제된 메뉴입니다. 트리를 다시 불러옵니다');
        await treeQuery.refetch();
        setDraft(null);
        setSelectedId(null);
        return;
      }
      handleApiError(error, toast);
    }
  };

  const handleIndent = (id: string) => {
    const next = indentMenuItem(items, id);
    if (!next) {
      toast.warning(
        hasChildMenus(items, id)
          ? '하위가 있는 대분류는 더 아래로 내릴 수 없습니다 (최대 2단계)'
          : '하위로 내릴 수 없습니다. 바로 위 대분류가 필요합니다.',
      );
      return;
    }
    setItems(next);
  };

  const handleOutdent = (id: string) => {
    const next = outdentMenuItem(items, id);
    if (!next) {
      toast.warning('이미 최상위 대분류입니다');
      return;
    }
    setItems(next);
  };

  const handleParentChange = (value: string) => {
    if (!draft) return;
    if (draft.hasChildren) {
      toast.warning('하위 메뉴가 있어 상위 메뉴를 변경할 수 없습니다');
      return;
    }
    const newParentId = value === ROOT_PARENT_VALUE ? null : value;
    if (newParentId === draft.parentId) return;

    if (newParentId !== null && hasChildMenus(items, draft.id)) {
      toast.warning(
        '하위가 있는 대분류는 다른 메뉴 아래로 옮길 수 없습니다',
      );
      return;
    }

    const next = reparentMenuItem(items, draft.id, newParentId);
    const applied = next.find((m) => m.id === draft.id);
    if (!applied || applied.parentId !== newParentId) {
      toast.warning('선택한 상위로 이동할 수 없습니다');
      return;
    }

    setItems(next);
    setDraft((prev) => (prev ? { ...prev, parentId: newParentId } : prev));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    setOverId(String(event.active.id));
    setOffsetLeft(0);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    setOffsetLeft(event.delta.x);
    if (event.over) {
      setOverId(String(event.over.id));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const currentOffset = offsetLeft;
    const rawOverId = over ? String(over.id) : activeId;

    setActiveId(null);
    setOverId(null);
    setOffsetLeft(0);

    if (!rawOverId) return;

    setItems((prev) => {
      const activeKey = String(active.id);
      const grouped = hasChildMenus(prev, activeKey);
      const dropOverId = resolveGroupedRootOverId(prev, activeKey, rawOverId);
      const offset = grouped ? 0 : currentOffset;
      const { items: next, projection: result } = applyMenuTreeDrag(
        prev,
        activeKey,
        dropOverId,
        offset,
      );
      if (result?.intent === 'blocked-nest') {
        queueMicrotask(() => toast.warning(result.message));
        return prev;
      }
      return next;
    });
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setOffsetLeft(0);
  };

  const patchDraft = (patch: Partial<MenuDraft>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const displayDepthOf = (item: MenuTreeItem): number => {
    if (projection && activeId === item.id) {
      return projection.depth;
    }
    return flatItems.find((m) => m.id === item.id)?.depth ?? 0;
  };

  const canIndentItem = (item: MenuTreeItem): boolean => {
    if (item.parentId !== null) return false;
    if (hasChildMenus(items, item.id)) return false;
    const index = items.findIndex((m) => m.id === item.id);
    return index > 0;
  };

  const parentLocked = Boolean(
    draft?.hasChildren || (draft && hasChildMenus(items, draft.id)),
  );
  const deleteDisabled = Boolean(
    !draft || draft.systemDefined || draft.hasChildren || deleteMenu.isPending,
  );

  if (treeQuery.isLoading && !hydrated) {
    return <LoadingSpinner tip="메뉴 트리를 불러오는 중..." />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Menu}
        title="메뉴 설정 관리"
        description="사이드바·권한 설정에 반영되는 메뉴 트리를 구성합니다"
      />

      <div
        role="note"
        className="flex w-full flex-col gap-1.5 rounded-[10px] bg-ruda-primary-50 px-4 py-3 text-[12px] text-primary"
      >
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            메뉴는 대분류 → 하위, 최대 2단계입니다. 드래그로 순서·계층을 바꾸거나
            우측에서 속성을 수정한 뒤 「저장」을 누르면 변경분만 서버에
            반영됩니다. 하위가 있는 대분류는 다른 메뉴의 하위로 옮길 수
            없습니다. 연결 경로는 앱에 정의된 라우트만 선택할 수 있습니다.
          </p>
        </div>
      </div>

      {unmappedMenus.length > 0 ? (
        <Alert variant="warning">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>
            앱 미등록 경로 {unmappedMenus.length}건 — 수정이 필요합니다
          </AlertTitle>
          <AlertDescription>
            <p className="mb-1.5">
              레거시 URL 등이 남아 사이드바 아이콘·이동이 맞지 않을 수 있습니다.
              트리의 「경로 수정」 배지 메뉴를 열어 프론트 정의 경로로 바꿔
              저장하세요.
            </p>
            <ul className="max-h-28 list-inside list-disc overflow-y-auto text-[12px]">
              {unmappedMenus.map((menu) => (
                <li key={menu.id}>
                  <button
                    type="button"
                    className="font-medium underline-offset-2 hover:underline"
                    onClick={() => selectItem(menu.id)}
                  >
                    {menu.name}
                  </button>
                  <span className="text-muted-foreground"> · {menu.url}</span>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col items-stretch gap-4 lg:flex-row lg:items-start">
        <div className="w-full overflow-hidden rounded-xl border border-border bg-background lg:w-[560px] lg:shrink-0">
          <div className="flex items-center justify-between gap-2 bg-muted/60 px-[18px] py-3.5">
            <div className="flex items-center gap-2">
              <h2 className="text-[13px] font-bold text-foreground">메뉴 트리</h2>
              {hasPendingChanges ? (
                <Badge variant="outline" className="text-[10px]">
                  편집 중
                </Badge>
              ) : null}
            </div>
            <MenuWriteGate>
              <Button
                type="button"
                size="sm"
                className="ruda-button h-auto px-3 py-[7px] text-xs"
                onClick={handleAddMenu}
              >
                <Plus className="h-3.5 w-3.5" />
                메뉴 추가
              </Button>
            </MenuWriteGate>
          </div>

          {projection && activeId ? (
            <div
              className={cn(
                'border-b border-border px-3.5 py-2 text-[11px] font-medium',
                isGroupedRootDrag
                  ? 'bg-ruda-primary-50 text-primary'
                  : intentBadgeClass(projection.intent),
              )}
              role="status"
            >
              {isGroupedRootDrag
                ? `대분류 블록 이동 · 하위 ${activeChildren.length}개가 함께 배치됩니다`
                : projection.message}
            </div>
          ) : null}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="max-h-[min(640px,70vh)] overflow-y-auto">
                {visibleItems.map((item) => {
                  const depth = displayDepthOf(item);
                  const children = childCountByParent.get(item.id) ?? 0;
                  const isActiveGroup =
                    activeId === item.id && children > 0;
                  const intent = projection?.intent;
                  const isOverRow =
                    !!activeId &&
                    resolvedOverId === item.id &&
                    intent !== 'blocked-nest';

                  const isNestParentTarget =
                    !isGroupedRootDrag &&
                    intent === 'nest' &&
                    projection?.nestTargetId === item.id;

                  const isDropIndicatorRow =
                    dropIndicator?.targetId === item.id;

                  let dropMode: MenuDropMode = 'none';
                  let dropEdge: MenuDropEdge = 'before';

                  if (isGroupedRootDrag) {
                    if (isDropIndicatorRow) {
                      dropMode = 'root-block';
                      dropEdge = dropIndicator?.edge ?? 'before';
                    }
                  } else if (intent === 'promote' && isDropIndicatorRow) {
                    dropMode = 'promote';
                    dropEdge = dropIndicator?.edge ?? 'before';
                  } else if (
                    intent === 'nest' &&
                    activeId !== item.id &&
                    (isOverRow || isNestParentTarget)
                  ) {
                    dropMode = 'nest';
                  } else if (intent === 'reorder' && isDropIndicatorRow) {
                    dropMode = 'reorder';
                    dropEdge = dropIndicator?.edge ?? 'before';
                  }

                  return (
                    <SortableMenuRowItem
                      key={item.id}
                      item={item}
                      depth={depth}
                      selected={
                        selectedId === item.id ||
                        (!!draft && draft.id === item.id)
                      }
                      childCount={children}
                      canIndent={canIndentItem(item)}
                      canOutdent={item.parentId !== null}
                      isNestTarget={isNestParentTarget}
                      dropMode={dropMode}
                      dropEdge={dropEdge}
                      dropLineDepth={
                        intent === 'nest'
                          ? Math.max(projection?.depth ?? 1, 1)
                          : intent === 'promote'
                            ? 0
                            : (projection?.depth ?? 0)
                      }
                      groupedDragging={isActiveGroup}
                      dragDisabled={!canWrite}
                      onSelect={selectItem}
                      onIndent={handleIndent}
                      onOutdent={handleOutdent}
                    />
                  );
                })}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeItem ? (
                <MenuRowPreview
                  item={activeItem}
                  depth={isGroupedRootDrag ? 0 : (projection?.depth ?? 0)}
                  projection={
                    isGroupedRootDrag
                      ? {
                          depth: 0,
                          parentId: null,
                          intent: 'reorder',
                          nestTargetId: null,
                          message: `대분류 블록 · 하위 ${activeChildren.length}개 함께`,
                        }
                      : projection
                  }
                  childrenItems={activeChildren}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4 rounded-xl border border-border bg-background p-5">
          {!draft ? (
            <div className="flex flex-1 flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                왼쪽 트리에서 메뉴를 선택하거나 「메뉴 추가」로 새 메뉴를
                만들어주세요.
              </p>
              {hasPendingChanges ? (
                <MenuWriteGate>
                  <div className="mt-auto flex items-center justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSaving}
                      onClick={handleCancel}
                    >
                      취소
                    </Button>
                    <Button
                      type="button"
                      className="ruda-button"
                      disabled={isSaving}
                      onClick={() => void handleSave()}
                    >
                      {isSaving ? '저장 중…' : '저장'}
                    </Button>
                  </div>
                </MenuWriteGate>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-[13px] font-bold text-foreground">
                  메뉴 상세
                </h2>
                {draft.parentMenuName ? (
                  <span className="text-[11px] text-muted-foreground">
                    상위: {draft.parentMenuName}
                  </span>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    최상위 메뉴
                  </span>
                )}
              </div>

              <Field
                label="메뉴명"
                htmlFor="menu-name"
                required
                hint="권한 설정·사이드바에 표시되는 이름"
                error={nameError}
              >
                <Input
                  id="menu-name"
                  value={draft.name}
                  onChange={(e) => {
                    patchDraft({ name: e.target.value });
                    if (nameError) setNameError(null);
                  }}
                  placeholder="메뉴명 입력"
                  maxLength={100}
                  disabled={readOnly}
                />
              </Field>

              <Field
                label="메뉴 키 (식별자)"
                htmlFor="menu-key"
                hint="서버가 생성한 고유 키 · 변경 불가"
              >
                <Input
                  id="menu-key"
                  value={draft.menuKey}
                  readOnly
                  className="bg-muted/60 text-muted-foreground"
                />
              </Field>

              {draftUrlUnmapped ? (
                <Alert variant="warning" className="mb-1">
                  <TriangleAlert className="h-4 w-4" />
                  <AlertTitle>연결 경로를 수정하세요</AlertTitle>
                  <AlertDescription>
                    현재 값 <code className="text-[12px]">{draft.url}</code> 은
                    앱 ROUTE_PATHS에 없습니다. 아래 목록에서 프론트 정의 경로를
                    고른 뒤 저장해야 사이드바에 아이콘·이동이 연결됩니다.
                  </AlertDescription>
                </Alert>
              ) : null}

              <Field
                label="연결 경로"
                htmlFor="menu-url"
                hint="앱에 정의된 라우트만 선택할 수 있습니다. 그룹/대분류는 「없음」"
                error={
                  draftUrlUnmapped
                    ? '미등록 경로입니다. 앱 라우트로 변경 후 저장하세요.'
                    : undefined
                }
              >
                <Select
                  value={toMenuUrlSelectValue(draft.url)}
                  onValueChange={(value) =>
                    patchDraft({ url: fromMenuUrlSelectValue(value) })
                  }
                  disabled={readOnly}
                >
                  <SelectTrigger
                    id="menu-url"
                    className={cn(
                      'w-full',
                      draftUrlUnmapped &&
                        'border-ruda-warning text-ruda-warning focus:ring-ruda-warning/30',
                    )}
                  >
                    <SelectValue placeholder="경로 선택" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {urlOptions.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        className={cn(
                          opt.unmapped && 'text-ruda-warning font-medium',
                        )}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field
                label="상위 메뉴"
                htmlFor="menu-parent"
                hint={
                  parentLocked
                    ? '하위 메뉴가 있어 상위 메뉴를 변경할 수 없습니다'
                    : '변경은 왼쪽 트리에도 바로 반영되며, 우측 「저장」으로 서버에 올립니다.'
                }
              >
                <Select
                  value={draft.parentId ?? ROOT_PARENT_VALUE}
                  onValueChange={handleParentChange}
                  disabled={readOnly || parentLocked}
                >
                  <SelectTrigger id="menu-parent" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {parentOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field
                label="노출 상태"
                hint={
                  <span className="flex flex-col gap-0.5">
                    <span>
                      숨김 시 사이드바에서만 제외되고, 메뉴 설정 화면에서는
                      계속 보입니다.
                    </span>
                    <span>
                      관리자 전용(ADMIN_ONLY)은 일반 계정 권한 설정에
                      노출되지 않습니다.
                    </span>
                  </span>
                }
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <SegmentedToggle
                    value={draft.visible ? 'visible' : 'hidden'}
                    options={[
                      { value: 'visible', label: '노출' },
                      { value: 'hidden', label: '숨김' },
                    ]}
                    onChange={(v) =>
                      patchDraft({ visible: v === 'visible' })
                    }
                    activeClassName="text-ruda-success"
                    disabled={readOnly}
                  />
                  <SegmentedToggle
                    value={draft.audience}
                    options={[
                      { value: 'admin', label: '관리자 전용' },
                      { value: 'general', label: '일반' },
                    ]}
                    onChange={(v) => patchDraft({ audience: v })}
                    activeClassName="text-ruda-success"
                    disabled={readOnly}
                  />
                </div>
              </Field>

              <div className="h-px w-full bg-border" />

              <MenuWriteGate>
                <div className="flex items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    disabled={deleteDisabled}
                    onClick={() => void handleDelete()}
                    title={
                      draft.systemDefined
                        ? '시스템 메뉴는 삭제할 수 없습니다'
                        : draft.hasChildren
                          ? '하위 메뉴가 있어 삭제할 수 없습니다'
                          : '메뉴 삭제'
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    삭제
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!hasPendingChanges || isSaving}
                      onClick={handleCancel}
                    >
                      취소
                    </Button>
                    <Button
                      type="button"
                      className="ruda-button"
                      disabled={!hasPendingChanges || isSaving}
                      onClick={() => void handleSave()}
                    >
                      {isSaving ? '저장 중…' : '저장'}
                    </Button>
                  </div>
                </div>
              </MenuWriteGate>
            </>
          )}
        </div>
      </div>

      <CreateMenuModal
        open={createOpen}
        items={items}
        defaultParentId={createDefaultParentId}
        submitting={createMenu.isPending}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreateMenu}
      />
    </div>
  );
};
